import React, { useState, useEffect, useRef } from "react";
import Layout from "../components/Layout";
import { useLanguage } from "../context/LanguageContext";
import { Fade } from "react-awesome-reveal";
import { Award, Zap, RefreshCw, Play, Shield, Heart, Crosshair, Menu, Volume2, VolumeX } from "react-feather";

const HIGH_SCORE_KEY = "dev_arcade_cyber_bug_high_score";
const MUTE_KEY = "dev_arcade_cyber_bug_muted";

function Game() {
  const { d } = useLanguage();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const hudRef = useRef(null);
  const audioCtxRef = useRef(null);

  const [gameState, setGameState] = useState("start"); // "start", "playing", "gameover"
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [weaponName, setWeaponName] = useState("Láser Básico");
  const [isCompact, setIsCompact] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const stateRef = useRef({
    gameState: "start",
    score: 0,
    lives: 3,
    level: 1,
    highScore: 0,
    playerX: 400,
    weaponLevel: 1, // 1: Single, 2: Double, 3: Triple Spread
    bullets: [],
    enemyBullets: [],
    enemies: [],
    powerups: [],
    particles: [],
    floatingTexts: [],
    keys: { left: false, right: false, space: false },
    lastSpawn: 0,
    lastPowerupSpawn: 0,
    lastFired: 0
  });

  const gameInfo = d.gameInfo || {
    title: "Cyber Bug Defender",
    subtitle: "¡Demuestra tus reflejos eliminando bugs y recolectando tecnologías!",
    score: "Puntuación",
    highScore: "Récord",
    lives: "Vidas",
    level: "Nivel",
    start: "Iniciar Juego",
    restart: "Jugar de Nuevo",
    gameOver: "¡Juego Terminado!",
    instructions: "Mueve tu nave deslizando el dedo o con las flechas. ¡Tu nave dispara automáticamente!"
  };

  useEffect(() => {
    const savedScore = localStorage.getItem(HIGH_SCORE_KEY);
    if (savedScore) {
      const parsed = parseInt(savedScore, 10);
      setHighScore(parsed);
      stateRef.current.highScore = parsed;
    }

    const savedMute = localStorage.getItem(MUTE_KEY);
    if (savedMute !== null) {
      setIsMuted(savedMute === "true");
    }
  }, []);

  const toggleMute = () => {
    setIsMuted((prev) => {
      const next = !prev;
      localStorage.setItem(MUTE_KEY, next.toString());
      return next;
    });
  };

  // Web Audio API Synthesizer (Zero External File Dependencies)
  const getAudioContext = () => {
    if (!audioCtxRef.current) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        audioCtxRef.current = new AudioCtx();
      }
    }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  };

  const playShootSFX = () => {
    if (isMuted) return;
    const ctx = getAudioContext();
    if (!ctx) return;

    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "triangle"; // Tono retro arcade equilibrado y nítido
      osc.frequency.setValueAtTime(620, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(240, ctx.currentTime + 0.05);

      gain.gain.setValueAtTime(0.05, ctx.currentTime); // Volumen óptimo equilibrado
      gain.gain.linearRampToValueAtTime(0.002, ctx.currentTime + 0.05);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.05);
    } catch (e) {
      // Audio autoplay policy fallback
    }
  };

  const playExplosionSFX = () => {
    if (isMuted) return;
    const ctx = getAudioContext();
    if (!ctx) return;

    try {
      const bufferSize = ctx.sampleRate * 0.18;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }

      const noise = ctx.createBufferSource();
      noise.buffer = buffer;

      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(550, ctx.currentTime);
      filter.frequency.linearRampToValueAtTime(70, ctx.currentTime + 0.18);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.18);

      noise.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      noise.start();
      noise.stop(ctx.currentTime + 0.18);
    } catch (e) {}
  };

  const playPowerupSFX = () => {
    if (isMuted) return;
    const ctx = getAudioContext();
    if (!ctx) return;

    try {
      const notes = [440, 554, 659, 880];
      notes.forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.05);

        gain.gain.setValueAtTime(0.12, ctx.currentTime + idx * 0.05);
        gain.gain.linearRampToValueAtTime(0.01, ctx.currentTime + (idx + 1) * 0.05);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(ctx.currentTime + idx * 0.05);
        osc.stop(ctx.currentTime + (idx + 1) * 0.05);
      });
    } catch (e) {}
  };

  const playHitSFX = () => {
    if (isMuted) return;
    const ctx = getAudioContext();
    if (!ctx) return;

    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(160, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(30, ctx.currentTime + 0.14);

      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.14);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.14);
    } catch (e) {}
  };

  const startGame = () => {
    getAudioContext();
    setScore(0);
    setLives(3);
    setLevel(1);
    setWeaponName("Láser Básico");
    setGameState("playing");

    const st = stateRef.current;
    st.gameState = "playing";
    st.score = 0;
    st.lives = 3;
    st.level = 1;
    st.weaponLevel = 1;
    st.bullets = [];
    st.enemyBullets = [];
    st.enemies = [];
    st.powerups = [];
    st.particles = [];
    st.floatingTexts = [];
    st.lastSpawn = Date.now();
    st.lastPowerupSpawn = Date.now();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d");

    let animationFrameId;

    const resizeCanvas = () => {
      const w = container.clientWidth || 800;
      const hudH = hudRef.current ? hudRef.current.offsetHeight : 55;
      setIsCompact(w < 580);

      canvas.width = w;
      const availableHeight = (window.innerHeight || document.documentElement.clientHeight) - hudH - 24;
      canvas.height = Math.max(260, availableHeight);
      
      if (stateRef.current.playerX > canvas.width) {
        stateRef.current.playerX = canvas.width / 2;
      }
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a") stateRef.current.keys.left = true;
      if (e.key === "ArrowRight" || e.key === "d") stateRef.current.keys.right = true;
      if (e.key === " " || e.key === "Spacebar") {
        stateRef.current.keys.space = true;
        fireBullet();
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a") stateRef.current.keys.left = false;
      if (e.key === "ArrowRight" || e.key === "d") stateRef.current.keys.right = false;
      if (e.key === " " || e.key === "Spacebar") stateRef.current.keys.space = false;
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const mouseX = (e.clientX - rect.left) * scaleX;
      stateRef.current.playerX = Math.max(30, Math.min(canvas.width - 30, mouseX));
    };

    const handleTouchMove = (e) => {
      if (e.cancelable) e.preventDefault();
      if (e.touches && e.touches[0]) {
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const touchX = (e.touches[0].clientX - rect.left) * scaleX;
        stateRef.current.playerX = Math.max(30, Math.min(canvas.width - 30, touchX));
      }
    };

    const handleTouchStart = (e) => {
      if (e.cancelable) e.preventDefault();
      if (e.touches && e.touches[0]) {
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const touchX = (e.touches[0].clientX - rect.left) * scaleX;
        stateRef.current.playerX = Math.max(30, Math.min(canvas.width - 30, touchX));
      }
      if (stateRef.current.gameState === "playing") {
        fireBullet();
      }
    };

    const handleMouseDown = () => {
      if (stateRef.current.gameState === "playing") {
        fireBullet();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("touchmove", handleTouchMove, { passive: false });
    canvas.addEventListener("touchstart", handleTouchStart, { passive: false });
    canvas.addEventListener("mousedown", handleMouseDown);

    const fireBullet = () => {
      const st = stateRef.current;
      if (st.gameState !== "playing") return;
      const now = Date.now();
      const fireRate = st.weaponLevel >= 3 ? 100 : (st.weaponLevel === 2 ? 130 : 160);
      if (now - st.lastFired < fireRate) return;
      st.lastFired = now;

      playShootSFX();

      const px = st.playerX;
      const py = canvas.height - 50;

      const spreadRatio = canvas.width / 800;
      const spreadVx = Math.min(2.5, Math.max(0.6, spreadRatio * 2.2));

      if (st.weaponLevel === 1) {
        st.bullets.push({ x: px, y: py, vx: 0, vy: -10, radius: 4, color: "#ff4d6d" });
      } else if (st.weaponLevel === 2) {
        const offset = Math.min(12, canvas.width * 0.025);
        st.bullets.push({ x: px - offset, y: py, vx: 0, vy: -10, radius: 5, color: "#06b6d4" });
        st.bullets.push({ x: px + offset, y: py, vx: 0, vy: -10, radius: 5, color: "#06b6d4" });
      } else {
        const offset = Math.min(10, canvas.width * 0.02);
        st.bullets.push({ x: px - offset, y: py, vx: -spreadVx, vy: -9.5, radius: 5, color: "#a855f7" });
        st.bullets.push({ x: px, y: py - 5, vx: 0, vy: -11, radius: 6, color: "#eab308" });
        st.bullets.push({ x: px + offset, y: py, vx: spreadVx, vy: -9.5, radius: 5, color: "#a855f7" });
      }
    };

    const techBadges = [
      { name: ".NET MAUI", weapon: "Láser Doble" },
      { name: "C++", weapon: "Disparo Triple" },
      { name: "Flutter", weapon: "Láser Doble" },
      { name: "React", weapon: "Disparo Triple" },
      { name: "ONNX", weapon: "Plasma Rápido" },
      { name: "C#", weapon: "Láser Doble" }
    ];

    const bugTypes = [
      { label: "BUG", color: "#ff4d4d", speed: 1.6, maxHp: 1, radius: 18, points: 20, zigzag: false, shooterChance: 0.15 },
      { label: "NULL_PTR", color: "#a855f7", speed: 1.9, maxHp: 2, radius: 22, points: 50, zigzag: true, shooterChance: 0.30 },
      { label: "MEM_LEAK", color: "#eab308", speed: 1.5, maxHp: 3, radius: 25, points: 80, zigzag: false, shooterChance: 0.35 },
      { label: "SYNTAX_ERR", color: "#06b6d4", speed: 2.2, maxHp: 5, radius: 28, points: 120, zigzag: true, shooterChance: 0.45 }
    ];

    const createExplosion = (x, y, color, count = 14) => {
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 5 + 1;
        stateRef.current.particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color,
          alpha: 1,
          size: Math.random() * 4 + 2
        });
      }
    };

    const addFloatingText = (text, x, y, color = "#ffffff") => {
      stateRef.current.floatingTexts.push({
        text,
        x,
        y,
        color,
        alpha: 1.2,
        vy: -1.2
      });
    };

    // Main 60fps Game Loop
    const loop = () => {
      const st = stateRef.current;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Cyber Grid Background
      ctx.strokeStyle = "rgba(204, 17, 34, 0.07)";
      ctx.lineWidth = 1;
      for (let x = 0; x < canvas.width; x += 45) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = 0; y < canvas.height; y += 45) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      if (st.gameState === "playing") {
        fireBullet();

        if (st.keys.left) st.playerX = Math.max(30, st.playerX - 7);
        if (st.keys.right) st.playerX = Math.min(canvas.width - 30, st.playerX + 7);

        // Spawn Enemies
        const now = Date.now();
        const spawnInterval = Math.max(650, 1400 - st.level * 60);
        if (now - st.lastSpawn > spawnInterval) {
          const type = bugTypes[Math.floor(Math.random() * bugTypes.length)];
          // A partir del nivel 10, TODOS los bugs son tiradores (100% shooter)
          const canShoot = st.level >= 10 ? true : (Math.random() < type.shooterChance);
          const bonusSpeed = Math.min(2.2, (st.level - 1) * 0.08);

          st.enemies.push({
            x: Math.random() * (canvas.width - 80) + 40,
            y: -30,
            radius: type.radius,
            speed: type.speed + bonusSpeed,
            label: type.label,
            color: type.color,
            hp: type.maxHp,
            maxHp: type.maxHp,
            points: type.points,
            zigzag: type.zigzag,
            canShoot,
            shotsFired: 0
          });
          st.lastSpawn = now;
        }

        // Spawn Tech Powerups
        if (now - st.lastPowerupSpawn > 5500) {
          const badgeObj = techBadges[Math.floor(Math.random() * techBadges.length)];
          st.powerups.push({
            x: Math.random() * (canvas.width - 120) + 60,
            y: -25,
            badge: badgeObj.name,
            weapon: badgeObj.weapon,
            speed: 1.6
          });
          st.lastPowerupSpawn = now;
        }

        // Update Player Bullets
        for (let i = st.bullets.length - 1; i >= 0; i--) {
          const b = st.bullets[i];
          b.x += b.vx;
          b.y += b.vy;
          if (b.y < -10 || b.x < -10 || b.x > canvas.width + 10) {
            st.bullets.splice(i, 1);
          }
        }

        // Update Enemy Bullets & Check Collisions
        const playerShipY = canvas.height - 32;
        for (let k = st.enemyBullets.length - 1; k >= 0; k--) {
          const eb = st.enemyBullets[k];
          eb.x += eb.vx;
          eb.y += eb.vy;

          const distToPlayer = Math.hypot(eb.x - st.playerX, eb.y - playerShipY);
          if (distToPlayer < eb.radius + 20) {
            createExplosion(st.playerX, playerShipY, "#ff4d4d", 22);
            playHitSFX();
            addFloatingText("-1 VIDA", st.playerX, playerShipY - 20, "#ff4d4d");
            st.enemyBullets.splice(k, 1);
            st.lives -= 1;
            setLives(st.lives);
            if (st.lives <= 0) {
              st.gameState = "gameover";
              setGameState("gameover");
            }
          } else if (eb.y > canvas.height + 10 || eb.x < -10 || eb.x > canvas.width + 10) {
            st.enemyBullets.splice(k, 1);
          }
        }

        // Update Enemies & Collisions
        for (let i = st.enemies.length - 1; i >= 0; i--) {
          const e = st.enemies[i];
          e.y += e.speed;

          if (e.zigzag) {
            e.x += Math.sin(e.y * 0.04) * 2.2;
            e.x = Math.max(e.radius + 10, Math.min(canvas.width - e.radius - 10, e.x));
          }

          // Disparo de Contraataque Dirigido
          // Conforme sube el nivel, disparan CADA VEZ MÁS TEMPRANO (cerca del borde superior al salir)
          const isHighLevel = st.level >= 10;
          const minY = Math.max(15, 60 - (st.level - 2) * 5); // A mayor nivel, menor es minY (disparan apenas entran a pantalla)
          const shootProbability = isHighLevel ? 0.08 : (0.008 + (st.level - 2) * 0.002);

          if (st.level >= 2 && e.canShoot && e.shotsFired < 1 && e.y > minY && e.y < canvas.height * 0.6) {
            if (Math.random() < shootProbability) {
              e.shotsFired = 1;
              const angle = Math.atan2(playerShipY - e.y, st.playerX - e.x);
              const speed = Math.min(6.2, 3.6 + (st.level - 1) * 0.15);

              st.enemyBullets.push({
                x: e.x,
                y: e.y + e.radius,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                radius: 4.5,
                color: e.color
              });
            }
          }

          for (let j = st.bullets.length - 1; j >= 0; j--) {
            const b = st.bullets[j];
            const dist = Math.hypot(e.x - b.x, e.y - b.y);
            if (dist < e.radius + b.radius) {
              createExplosion(b.x, b.y, b.color, 6);
              e.hp -= 1;
              st.bullets.splice(j, 1);

              if (e.hp <= 0) {
                createExplosion(e.x, e.y, e.color, 18);
                playExplosionSFX();
                addFloatingText("+" + e.points, e.x, e.y, e.color);
                st.score += e.points;
                setScore(st.score);
                if (st.score > st.highScore) {
                  st.highScore = st.score;
                  setHighScore(st.score);
                  localStorage.setItem(HIGH_SCORE_KEY, st.score.toString());
                }
                const newLevel = Math.floor(st.score / 400) + 1;
                if (newLevel !== st.level) {
                  st.level = newLevel;
                  setLevel(newLevel);
                  addFloatingText("¡NIVEL " + newLevel + "!", canvas.width / 2, 100, "#cc1122");
                }
                st.enemies.splice(i, 1);
              } else {
                addFloatingText("-1 HP", e.x, e.y - 15, "#ffffff");
              }
              break;
            }
          }

          if (e && e.y > canvas.height - 20) {
            createExplosion(e.x, e.y, "#ff4d4d", 20);
            playHitSFX();
            st.enemies.splice(i, 1);
            st.lives -= 1;
            setLives(st.lives);
            if (st.lives <= 0) {
              st.gameState = "gameover";
              setGameState("gameover");
            }
          }
        }

        // Update Powerups
        for (let i = st.powerups.length - 1; i >= 0; i--) {
          const p = st.powerups[i];
          p.y += p.speed;

          if (Math.abs(p.x - st.playerX) < 45 && Math.abs(p.y - playerShipY) < 30) {
            createExplosion(p.x, p.y, "#22c55e", 20);
            playPowerupSFX();
            st.score += 100;
            setScore(st.score);

            if (st.weaponLevel < 3) {
              st.weaponLevel += 1;
            }
            const name = st.weaponLevel === 2 ? "Láser Doble ⚡" : "Disparo Triple 🔥";
            setWeaponName(name);
            addFloatingText("¡MEJORA: " + p.badge + "!", p.x, p.y - 10, "#22c55e");

            if (st.score > st.highScore) {
              st.highScore = st.score;
              setHighScore(st.score);
              localStorage.setItem(HIGH_SCORE_KEY, st.score.toString());
            }
            st.powerups.splice(i, 1);
          } else if (p.y > canvas.height + 30) {
            st.powerups.splice(i, 1);
          }
        }
      }

      // Draw Floating Texts
      for (let i = st.floatingTexts.length - 1; i >= 0; i--) {
        const ft = st.floatingTexts[i];
        ft.y += ft.vy;
        ft.alpha -= 0.025;
        if (ft.alpha <= 0) {
          st.floatingTexts.splice(i, 1);
        } else {
          ctx.save();
          ctx.globalAlpha = Math.max(0, ft.alpha);
          ctx.fillStyle = ft.color;
          ctx.font = "bold 14px sans-serif";
          ctx.textAlign = "center";
          ctx.fillText(ft.text, ft.x, ft.y);
          ctx.restore();
        }
      }

      // Draw Particles
      for (let i = st.particles.length - 1; i >= 0; i--) {
        const pt = st.particles[i];
        pt.x += pt.vx;
        pt.y += pt.vy;
        pt.alpha -= 0.035;
        if (pt.alpha <= 0) {
          st.particles.splice(i, 1);
        } else {
          ctx.save();
          ctx.globalAlpha = Math.max(0, pt.alpha);
          ctx.fillStyle = pt.color;
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, pt.size, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }

      // Draw Player Bullets
      ctx.shadowBlur = 10;
      for (const b of st.bullets) {
        ctx.shadowColor = b.color;
        ctx.fillStyle = b.color;
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw Enemy Bullets
      ctx.shadowBlur = 12;
      for (const eb of st.enemyBullets) {
        ctx.shadowColor = eb.color;
        ctx.fillStyle = eb.color;
        ctx.beginPath();
        ctx.arc(eb.x, eb.y, eb.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw Enemies & HP Bars
      for (const e of st.enemies) {
        ctx.shadowBlur = 14;
        ctx.shadowColor = e.color;
        ctx.fillStyle = e.color;
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.radius, 0, Math.PI * 2);
        ctx.fill();

        ctx.shadowBlur = 0;
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 10px monospace";
        ctx.textAlign = "center";
        ctx.fillText(e.label, e.x, e.y + 3);

        if (e.maxHp > 1) {
          const barWidth = e.radius * 1.8;
          const barHeight = 4;
          const barX = e.x - barWidth / 2;
          const barY = e.y - e.radius - 8;

          ctx.fillStyle = "rgba(0,0,0,0.6)";
          ctx.fillRect(barX, barY, barWidth, barHeight);

          const hpWidth = (e.hp / e.maxHp) * barWidth;
          ctx.fillStyle = e.hp > 1 ? "#22c55e" : "#ff4d4d";
          ctx.fillRect(barX, barY, hpWidth, barHeight);
        }
      }

      // Draw Powerups (Tech Badges)
      for (const p of st.powerups) {
        ctx.shadowBlur = 16;
        ctx.shadowColor = "#22c55e";
        ctx.fillStyle = "#15803d";
        ctx.beginPath();
        ctx.roundRect(p.x - 45, p.y - 15, 90, 30, 8);
        ctx.fill();

        ctx.shadowBlur = 0;
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 11px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("⚡ " + p.badge, p.x, p.y + 4);
      }

      // Draw Player Defender Spaceship
      const px = st.playerX;
      const py = canvas.height - 32;

      ctx.shadowBlur = 18;
      ctx.shadowColor = "#cc1122";
      ctx.fillStyle = "#cc1122";

      // Cannon body
      ctx.beginPath();
      ctx.moveTo(px, py - 22);
      ctx.lineTo(px - 24, py + 14);
      ctx.lineTo(px + 24, py + 14);
      ctx.closePath();
      ctx.fill();

      // Wing cannons if upgraded
      if (st.weaponLevel >= 2) {
        ctx.fillStyle = "#06b6d4";
        ctx.fillRect(px - 26, py + 2, 4, 10);
        ctx.fillRect(px + 22, py + 2, 4, 10);
      }

      // Core glow
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(px, py - 4, 5, 0, Math.PI * 2);
      ctx.fill();

      animationFrameId = requestAnimationFrame(loop);
    };

    loop();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchstart", handleTouchStart);
      canvas.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isMuted]);

  return (
    <Layout>
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100dvh",
          maxHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          padding: "4px",
          boxSizing: "border-box",
          position: "relative",
          overflow: "hidden",
          touchAction: "none",
          zIndex: 1
        }}
      >
        {/* Fullscreen Arcade HUD */}
        <div
          ref={hudRef}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: isCompact ? "6px" : "10px",
            background: "rgba(25, 29, 43, 0.96)",
            border: "1px solid rgba(204, 17, 34, 0.4)",
            borderRadius: "10px 10px 0 0",
            padding: isCompact ? "6px 10px" : "8px 16px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            overflowX: "auto",
            touchAction: "none",
            zIndex: 2
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: isCompact ? "6px" : "10px", flexShrink: 0 }}>
            {/* Botón Menú */}
            <button
              onClick={() => window.dispatchEvent(new Event('toggle-menu'))}
              style={{
                background: "rgba(204, 17, 34, 0.2)",
                border: "1px solid rgba(204, 17, 34, 0.6)",
                color: "#ffffff",
                padding: isCompact ? "6px" : "6px 12px",
                width: isCompact ? "32px" : "auto",
                height: isCompact ? "32px" : "auto",
                borderRadius: "6px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: isCompact ? "0px" : "6px",
                fontSize: "13px",
                fontWeight: "bold",
                flexShrink: 0
              }}
            >
              <Menu size={16} color="#cc1122" />
              {!isCompact && <span>Menú</span>}
            </button>

            {/* Botón Mute / Unmute Audio SFX */}
            <button
              onClick={toggleMute}
              title={isMuted ? "Activar Sonido" : "Silenciar"}
              style={{
                background: isMuted ? "rgba(239, 68, 68, 0.2)" : "rgba(34, 197, 94, 0.2)",
                border: isMuted ? "1px solid rgba(239, 68, 68, 0.6)" : "1px solid rgba(34, 197, 94, 0.6)",
                color: "#ffffff",
                padding: isCompact ? "6px" : "6px 10px",
                width: isCompact ? "32px" : "auto",
                height: isCompact ? "32px" : "auto",
                borderRadius: "6px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                fontSize: "12px",
                fontWeight: "bold",
                flexShrink: 0
              }}
            >
              {isMuted ? <VolumeX size={16} color="#ff4d4d" /> : <Volume2 size={16} color="#22c55e" />}
              {!isCompact && <span>{isMuted ? "Muted" : "SFX"}</span>}
            </button>

            <div>
              <span style={{ fontSize: "10px", color: "#a4acc4", display: "block" }}>{isCompact ? "Pts" : gameInfo.score}</span>
              <strong style={{ fontSize: isCompact ? "14px" : "18px", color: "#ffffff" }}>{score}</strong>
            </div>
            <div>
              <span style={{ fontSize: "10px", color: "#a4acc4", display: "block" }}>{isCompact ? "Lv" : gameInfo.level}</span>
              <strong style={{ fontSize: isCompact ? "14px" : "18px", color: "#cc1122" }}>Lv.{level}</strong>
            </div>
            {!isCompact && (
              <div style={{ borderLeft: "1px solid rgba(255,255,255,0.1)", paddingLeft: "10px" }}>
                <span style={{ fontSize: "10px", color: "#a4acc4", display: "block" }}>Arma</span>
                <strong style={{ fontSize: "12px", color: "#22c55e", display: "flex", alignItems: "center", gap: "3px" }}>
                  <Crosshair size={12} /> {weaponName}
                </strong>
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
            {!isCompact && <span style={{ fontSize: "11px", color: "#a4acc4" }}>{gameInfo.lives}:</span>}
            {[...Array(3)].map((_, i) => (
              <Heart
                key={i}
                size={isCompact ? 14 : 16}
                fill={i < lives ? "#cc1122" : "transparent"}
                color={i < lives ? "#cc1122" : "#555"}
              />
            ))}
          </div>

          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <span style={{ fontSize: "10px", color: "#a4acc4", display: "flex", alignItems: "center", gap: "3px", justifyContent: "flex-end" }}>
              <Award size={12} color="#eab308" /> {isCompact ? "Réc" : gameInfo.highScore}
            </span>
            <strong style={{ fontSize: isCompact ? "14px" : "17px", color: "#eab308" }}>{highScore}</strong>
          </div>
        </div>

        {/* Fullscreen Canvas Container */}
        <div style={{ position: "relative", flex: 1, width: "100%", overflow: "hidden", borderRadius: "0 0 10px 10px", background: "#0c0e14", border: "1px solid rgba(204, 17, 34, 0.4)", borderTop: "none", touchAction: "none" }}>
          <canvas
            ref={canvasRef}
            style={{ width: "100%", height: "100%", display: "block", cursor: "crosshair", touchAction: "none" }}
          />

          {/* Start Modal Overlay */}
          {gameState === "start" && (
            <div style={{
              position: "absolute",
              inset: 0,
              background: "rgba(12, 14, 20, 0.9)",
              backdropFilter: "blur(8px)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
              textAlign: "center",
              touchAction: "none"
            }}>
              <Shield size={50} color="#cc1122" style={{ marginBottom: "12px" }} />
              <h2 style={{ color: "#ffffff", marginBottom: "10px", fontSize: "24px" }}>{gameInfo.title}</h2>
              <p style={{ color: "#a4acc4", maxWidth: "480px", marginBottom: "20px", fontSize: "13px", lineHeight: "1.5" }}>
                {gameInfo.instructions}
              </p>
              <button className="mi-button" onClick={startGame} style={{ padding: "10px 32px", fontSize: "15px" }}>
                <Play size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />
                {gameInfo.start}
              </button>
            </div>
          )}

          {/* Game Over Modal Overlay */}
          {gameState === "gameover" && (
            <div style={{
              position: "absolute",
              inset: 0,
              background: "rgba(12, 14, 20, 0.94)",
              backdropFilter: "blur(10px)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
              textAlign: "center",
              touchAction: "none"
            }}>
              <Zap size={50} color="#ff4d4d" style={{ marginBottom: "12px" }} />
              <h2 style={{ color: "#ff4d4d", marginBottom: "10px", fontSize: "24px" }}>{gameInfo.gameOver}</h2>
              <p style={{ color: "#ffffff", fontSize: "16px", marginBottom: "4px" }}>
                {gameInfo.score}: <strong>{score}</strong>
              </p>
              <p style={{ color: "#eab308", fontSize: "15px", marginBottom: "20px" }}>
                {gameInfo.highScore}: <strong>{highScore}</strong>
              </p>
              <button className="mi-button" onClick={startGame} style={{ padding: "10px 32px", fontSize: "15px" }}>
                <RefreshCw size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />
                {gameInfo.restart}
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Game;
